import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getLocations } from 'wasp/client/operations';
import { useParams, useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import type { Location as WaspLocation } from 'wasp/entities';
import { findLocationBySlug, getLocationSlugFromPath, locationScopedPath } from '../lib/locationPaths';
import { isHubLandingPath } from '../../shared/hubLocations';

// Extended Location type with hierarchy fields
export interface LocationWithHierarchy extends WaspLocation {
  id: string;
  name: string;
  locationCode: string | null;
  isHub: boolean;
  hierarchyLevel: number;
  parentLocationId: string | null;
  parentLocation?: { id: string; name: string; locationCode: string } | null;
  childLocations?: { id: string; name: string; locationCode: string; isHub?: boolean }[];
  _count?: { childLocations: number };
  allChildLocationIds?: string[];
  membership?: { isSubscribed: boolean; isVisibleInNav?: boolean } | null;
}

interface LocationContextType {
  selectedLocation: LocationWithHierarchy | null;
  setSelectedLocation: (location: LocationWithHierarchy | null) => void;
  locations: LocationWithHierarchy[];
  isLoading: boolean;
  error: any;
  navigateToLocationPage: (path: string) => void;
  // Hub filtering
  childLocationFilter: string | null;
  setChildLocationFilter: (locationId: string | null) => void;
  isHubSelected: boolean;
  childLocations: LocationWithHierarchy[];
  // Helper to get all location IDs for current selection (for queries)
  getLocationIdsForQuery: () => string[];
  // Advanced hub filtering
  filteredChildLocationIds: string[];
  setFilteredChildLocationIds: (ids: string[]) => void;
  clearLocationFilter: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationProviderProps {
  children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
  const [selectedLocation, setSelectedLocationInternal] = useState<LocationWithHierarchy | null>(() => {
    // Try to restore from localStorage on initial load
    const saved = localStorage.getItem('selectedLocation');
    return saved ? JSON.parse(saved) : null;
  });
  const [childLocationFilter, setChildLocationFilter] = useState<string | null>(null);
  const [filteredChildLocationIds, setFilteredChildLocationIdsInternal] = useState<string[]>(() => {
    // Try to restore from localStorage on initial load
    const saved = localStorage.getItem('filteredChildLocationIds');
    return saved ? JSON.parse(saved) : [];
  });
  const params = useParams<{ slug?: string }>();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();

  // Fetch available locations
  const { data: locationsData, isLoading, error } = useQuery(getLocations);
  const locations = (locationsData as unknown as LocationWithHierarchy[]) || [];

  // Custom setter that clears child filter when location changes
  const setSelectedLocation = (location: LocationWithHierarchy | null) => {
    setSelectedLocationInternal(location);
    setChildLocationFilter(null); // Clear filter when switching locations
    // Don't clear filteredChildLocationIds - keep filter persistent
  };

  // Custom setter for filtered child locations with persistence
  const setFilteredChildLocationIds = (ids: string[]) => {
    setFilteredChildLocationIdsInternal(ids);
    localStorage.setItem('filteredChildLocationIds', JSON.stringify(ids));
  };

  // Clear all location filters
  const clearLocationFilter = () => {
    setChildLocationFilter(null);
    setFilteredChildLocationIdsInternal([]);
    localStorage.removeItem('filteredChildLocationIds');
  };

  // Persist selected location to localStorage whenever it changes
  useEffect(() => {
    if (selectedLocation) {
      localStorage.setItem('selectedLocation', JSON.stringify(selectedLocation));
    }
  }, [selectedLocation]);

  // Keep selectedLocation in sync when locations data refetches
  useEffect(() => {
    if (!locations.length) return;

    setSelectedLocationInternal((prev) => {
      if (!prev) return prev;
      const fresh = locations.find((l) => l.id === prev.id);
      if (!fresh) return locations[0] ?? null;
      if (JSON.stringify(fresh.membership) !== JSON.stringify(prev.membership)) {
        return { ...prev, membership: fresh.membership };
      }
      return prev;
    });
  }, [locations]);

  const navigateToLocationPage = (path: string) => {
    navigate(locationScopedPath(selectedLocation, path));
  };

  // Set initial selected location based on URL params or first available location
  useEffect(() => {
    if (locations && Array.isArray(locations) && locations.length > 0) {
      // Canada (locationCode: 'CA') is the default fallback location
      const canadaLocation = locations.find(loc => loc.locationCode === 'CA') ?? locations[0];

      const slugFromPath = getLocationSlugFromPath(routerLocation.pathname);
      const effectiveSlug = params.slug ?? slugFromPath;

      if (effectiveSlug) {
        // Match URL slug against location.slug or locationCode (e.g. bayviewyards)
        const locationBySlug = findLocationBySlug(locations, effectiveSlug);
        if (locationBySlug) {
          setSelectedLocationInternal(locationBySlug);
        } else if (effectiveSlug === 'CA-ALL') {
          // Default to Canada (National) for the "All" view
          setSelectedLocationInternal(canadaLocation);
        } else if (!selectedLocation) {
          // slug not a location code (e.g. org slug) and no location selected yet — default to Canada
          setSelectedLocationInternal(canadaLocation);
        }
        // If slug doesn't match a location but we already have a selectedLocation, keep it
      } else if (!selectedLocation) {
        // No slug in URL and no selected location, default to Canada
        setSelectedLocationInternal(canadaLocation);
      }
    }
  }, [locations, params.slug, routerLocation.pathname]);

  // Reset selected location when navigating to non-location-specific pages
  useEffect(() => {
    if (isHubLandingPath(routerLocation.pathname)) {
      return;
    }

    const isLocationSpecificRoute = routerLocation.pathname.includes('/locations/') ||
                                   routerLocation.pathname.includes('/tenants') ||
                                   routerLocation.pathname.includes('/notices') ||
                                   routerLocation.pathname.includes('/events') ||
                                   routerLocation.pathname.includes('/facilities') ||
                                   routerLocation.pathname.includes('/buy-sell') ||
                                   routerLocation.pathname.includes('/food-court') ||
                                   routerLocation.pathname.includes('/lost-found') ||
                                   routerLocation.pathname.includes('/services') ||
                                   routerLocation.pathname.includes('/request-offer') ||
                                   routerLocation.pathname.includes('/transportation') ||
                                   routerLocation.pathname.includes('/information-desk') ||
                                   routerLocation.pathname.includes('/organizations') ||
                                   routerLocation.pathname.includes('/organization-types') ||
                                   routerLocation.pathname.includes('/dashboard') ||
                                   routerLocation.pathname.includes('/admin');

    if (!isLocationSpecificRoute && locations.length > 0) {
      const canadaLocation = locations.find(loc => loc.locationCode === 'CA') ?? locations[0];
      setSelectedLocationInternal(canadaLocation);
    }
  }, [routerLocation.pathname, locations]);

  // Compute if current selection is a hub
  const isHubSelected = useMemo(() => {
    return selectedLocation?.isHub === true;
  }, [selectedLocation]);

  // Get child locations for the currently selected hub
  const childLocations = useMemo(() => {
    if (!selectedLocation?.isHub) return [];
    // Get all locations that have this location as parent
    return locations.filter(loc => loc.parentLocationId === selectedLocation.id);
  }, [selectedLocation, locations]);

  // Helper to get all location IDs for queries (handles hub aggregation)
  const getLocationIdsForQuery = (): string[] => {
    if (!selectedLocation) return [];

    if (childLocationFilter) {
      // Specific child location filter is active
      return [childLocationFilter];
    }

    // Check if custom filter is applied for hub
    if (selectedLocation.isHub && filteredChildLocationIds.length > 0) {
      return filteredChildLocationIds;
    }

    if (selectedLocation.isHub && selectedLocation.allChildLocationIds) {
      // Return hub ID + all descendant IDs
      return [selectedLocation.id, ...selectedLocation.allChildLocationIds];
    }

    // Regular location
    return [selectedLocation.id];
  };

  const value: LocationContextType = {
    selectedLocation,
    setSelectedLocation,
    locations,
    isLoading,
    error,
    navigateToLocationPage,
    // Hub filtering
    childLocationFilter,
    setChildLocationFilter,
    isHubSelected,
    childLocations,
    getLocationIdsForQuery,
    // Advanced hub filtering
    filteredChildLocationIds,
    setFilteredChildLocationIds,
    clearLocationFilter
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
}

// Keep this for backwards compatibility but prefer useLocationContext
export function useLocation(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}


